import { useCallback, useEffect, useRef, useState } from "react";
import {
  buildAuthEmailRedirectUrl,
  buildRegistrationLegalConsents,
  getSignupErrorI18nKey,
  logAuthErrorDev,
  normalizeSignupIntent,
  resendSignupConfirmationEmail,
  signUpWithPassword,
  validatePublicRegistration,
} from "@tennis-matches/services/auth";
import {
  applyPendingSignupAccessRequestRemote,
  checkSignupEmailAvailableRemote,
  REGISTRATION_ONBOARDING_OUTCOME,
} from "@tennis-matches/services/governance";
import { navigateTo } from "@tennis-matches/core/router";
import {
  clearRegisterWizardDraft,
  loadRegisterWizardDraft,
  saveRegisterWizardDraft,
} from "../utils/register-wizard-draft.js";

const REGISTRATION_NEXT_ROUTE = "hub";

const FIELD_KEYS = [
  "email",
  "password",
  "password2",
  "firstName",
  "lastName",
  "signupIntent",
  "consentPrivacy",
  "consentTerms",
];

function __asStr(v) {
  return String(v ?? "").trim();
}

function __formatRegistrationFullName(firstName, lastName) {
  const first = String(firstName || "").trim();
  const last = String(lastName || "").trim();
  if (!first) return "";
  return last ? `${first} ${last}` : first;
}

function __markRegistrationOnboardingNotice(applied) {
  try {
    if (applied?.ok && applied?.outcome === REGISTRATION_ONBOARDING_OUTCOME) {
      sessionStorage.setItem("tm_registration_onboarding_notice", "1");
    }
  } catch (_) {}
}

function __markPostRegistrationAccessPending(applied) {
  try {
    if (applied?.ok && applied?.skipped !== true) {
      sessionStorage.setItem("tm_post_registration_access_pending", "1");
    }
  } catch (_) {}
}

/**
 * Public registration form state, draft, validation, email-check, signup.
 *
 * @param {{
 *   t: (key: string, params?: object) => string,
 *   invitedEmail?: string,
 *   draftEnabled?: boolean,
 *   onRegistered?: () => void,
 *   onDraftHydrated?: () => void,
 * }} options
 */
export function usePublicRegistrationForm({
  t,
  invitedEmail = "",
  draftEnabled = true,
  onRegistered,
  onDraftHydrated,
} = {}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [signupIntent, setSignupIntent] = useState("");
  const [consentPrivacy, setConsentPrivacy] = useState(false);
  const [consentTerms, setConsentTerms] = useState(false);

  const [inlineErrors, setInlineErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [emailCheckBusy, setEmailCheckBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [successEmail, setSuccessEmail] = useState("");
  const [resendBusy, setResendBusy] = useState(false);
  const [resendNotice, setResendNotice] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  const inFlightRef = useRef(false);
  const draftHydratedRef = useRef(false);

  useEffect(() => {
    if (draftHydratedRef.current || invitedEmail) return;
    draftHydratedRef.current = true;
    const draft = loadRegisterWizardDraft();
    if (!draft) return;
    setEmail(__asStr(draft.regEmail));
    setFirstName(__asStr(draft.regFirstName));
    setLastName(__asStr(draft.regLastName));
    setSignupIntent(normalizeSignupIntent(draft.regSignupIntent) || "");
    setConsentPrivacy(Boolean(draft.regConsentPrivacy));
    setConsentTerms(Boolean(draft.regConsentTerms));
    if (typeof onDraftHydrated === "function") onDraftHydrated();
  }, [invitedEmail, onDraftHydrated]);

  useEffect(() => {
    if (!draftEnabled || invitedEmail || isSuccess) return undefined;
    const timer = setTimeout(() => {
      saveRegisterWizardDraft({
        regEmail: email,
        regFirstName: firstName,
        regLastName: lastName,
        regSignupIntent: signupIntent,
        regConsentPrivacy: consentPrivacy,
        regConsentTerms: consentTerms,
      });
    }, 400);
    return () => clearTimeout(timer);
  }, [
    draftEnabled,
    invitedEmail,
    isSuccess,
    email,
    firstName,
    lastName,
    signupIntent,
    consentPrivacy,
    consentTerms,
  ]);

  const clearField = useCallback((key) => {
    setInlineErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const clearError = useCallback(() => setError(""), []);

  const resetConsents = useCallback(() => {
    setConsentPrivacy(false);
    setConsentTerms(false);
  }, []);

  const leaveSuccess = useCallback(() => {
    setIsSuccess(false);
    setSuccessEmail("");
    setResendNotice("");
  }, []);

  const applyLiveEmailStatus = useCallback(
    (status) => {
      if (status === "taken") {
        setInlineErrors((prev) => ({
          ...prev,
          email: t("auth.signupError.userAlreadyRegistered"),
        }));
        return;
      }
      if (status === "free" || status === "idle") {
        setInlineErrors((prev) => {
          if (!prev.email) return prev;
          const next = { ...prev };
          delete next.email;
          return next;
        });
      }
    },
    [t],
  );

  const validate = useCallback(() => {
    const result = validatePublicRegistration({
      email,
      password,
      password2,
      firstName,
      lastName,
      signupIntent,
      consentPrivacy,
      consentTerms,
    });
    const translated = {};
    for (const [key, i18nKey] of Object.entries(result.errors)) {
      translated[key] = t(i18nKey);
    }
    setInlineErrors((prev) => {
      const merged = { ...prev };
      FIELD_KEYS.forEach((k) => delete merged[k]);
      if (result.ok) return merged;
      return { ...merged, ...translated };
    });
    return result.ok;
  }, [
    email,
    password,
    password2,
    firstName,
    lastName,
    signupIntent,
    consentPrivacy,
    consentTerms,
    t,
  ]);

  const onSubmit = useCallback(async () => {
    if (busy || emailCheckBusy || inFlightRef.current) return;
    clearError();
    if (!validate()) return;

    setEmailCheckBusy(true);
    try {
      const res = await checkSignupEmailAvailableRemote(email);
      if (res?.rateLimited) {
        setInlineErrors((prev) => ({
          ...prev,
          email: t("auth.register.emailCheck.rateLimited", {
            seconds: String(res.retryAfterSec || 20),
          }),
        }));
        return;
      }
      if (res?.ok && res.available === false) {
        setInlineErrors((prev) => ({
          ...prev,
          email: t("auth.signupError.userAlreadyRegistered"),
        }));
        return;
      }
    } finally {
      setEmailCheckBusy(false);
    }

    inFlightRef.current = true;
    setBusy(true);
    try {
      const normalizedEmail = String(email || "")
        .trim()
        .toLowerCase();
      const fullName = __formatRegistrationFullName(firstName, lastName);
      const intent = normalizeSignupIntent(signupIntent);

      const { data, error: e } = await signUpWithPassword({
        email: normalizedEmail,
        password: String(password || ""),
        options: {
          emailRedirectTo: buildAuthEmailRedirectUrl(
            `/${REGISTRATION_NEXT_ROUTE}`,
          ),
          data: {
            full_name: fullName,
            signup_intent: intent,
            legal_consents: buildRegistrationLegalConsents({
              privacyAccepted: consentPrivacy,
              termsAccepted: consentTerms,
            }),
          },
        },
      });

      if (e) {
        logAuthErrorDev("signup/password", e, { endpoint: "auth/v1/signup" });
        const userCreatedWithoutSession =
          Boolean(data?.user?.id) && !Boolean(data?.session?.user?.id);
        if (userCreatedWithoutSession) {
          clearRegisterWizardDraft();
          setSuccessEmail(normalizedEmail);
          setResendNotice("");
          setIsSuccess(true);
          return;
        }
        setError(t(getSignupErrorI18nKey(e)));
        return;
      }

      const hasSession = Boolean(data?.session?.user?.id);
      if (hasSession) {
        try {
          const applied = await applyPendingSignupAccessRequestRemote();
          if (applied?.ok && applied?.skipped !== true) {
            __markRegistrationOnboardingNotice(applied);
            __markPostRegistrationAccessPending(applied);
          }
        } catch (_) {}
        clearRegisterWizardDraft();
        if (typeof onRegistered === "function") onRegistered();
        else navigateTo(REGISTRATION_NEXT_ROUTE, true);
        return;
      }

      clearRegisterWizardDraft();
      setSuccessEmail(normalizedEmail);
      setResendNotice("");
      setIsSuccess(true);
    } catch (e) {
      logAuthErrorDev("signup/password/catch", e, {
        endpoint: "auth/v1/signup",
      });
      setError(t(getSignupErrorI18nKey(e)));
    } finally {
      inFlightRef.current = false;
      setBusy(false);
    }
  }, [
    busy,
    emailCheckBusy,
    clearError,
    validate,
    email,
    password,
    firstName,
    lastName,
    signupIntent,
    consentPrivacy,
    consentTerms,
    t,
    onRegistered,
  ]);

  const onResendEmail = useCallback(async () => {
    if (!successEmail || resendBusy) return;
    setResendBusy(true);
    setResendNotice("");
    try {
      const res = await resendSignupConfirmationEmail(successEmail, {
        nextPath: `/${REGISTRATION_NEXT_ROUTE}`,
      });
      if (res?.ok) {
        setResendNotice(t("auth.register.success.resendOk"));
      } else {
        setResendNotice(t("auth.register.success.resendFailed"));
      }
    } catch {
      setResendNotice(t("auth.register.success.resendFailed"));
    } finally {
      setResendBusy(false);
    }
  }, [successEmail, resendBusy, t]);

  const onUseDifferentEmail = useCallback(() => {
    setSuccessEmail("");
    setResendNotice("");
    setEmail("");
    setPassword("");
    setPassword2("");
    setError("");
    setIsSuccess(false);
  }, []);

  const formBusy = busy || emailCheckBusy;

  const formProps = {
    t,
    busy: formBusy ? "register-public" : "",
    values: {
      email,
      password,
      password2,
      firstName,
      lastName,
      signupIntent,
      consentPrivacy,
      consentTerms,
    },
    setters: {
      setEmail,
      setPassword,
      setPassword2,
      setFirstName,
      setLastName,
      setSignupIntent,
      setConsentPrivacy,
      setConsentTerms,
    },
    inlineErrors,
    clearField,
    onSubmit,
    showPassword,
    showPassword2,
    toggleShowPassword: () => setShowPassword((v) => !v),
    toggleShowPassword2: () => setShowPassword2((v) => !v),
  };

  const successProps = {
    email: successEmail,
    onResendEmail,
    onUseDifferentEmail,
    resendBusy,
    resendNotice,
  };

  return {
    formProps,
    successProps,
    email,
    setEmail,
    busy: formBusy,
    error,
    clearError,
    setError,
    isSuccess,
    leaveSuccess,
    resetConsents,
    clearDraft: clearRegisterWizardDraft,
    applyLiveEmailStatus,
    draftWouldOpenRegister: () => Boolean(loadRegisterWizardDraft()),
  };
}
